import React, { useRef, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Dialog } from 'primereact/dialog';
import html2canvas from 'html2canvas';
import { 
  Download, 
  Info, 
  Image,
  FileText,
  Eye
} from 'lucide-react';

const SCurveChartData = ({ data, selectedYear }) => {
  const chartRef = useRef(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Check if all data arrays are empty
  const isEmpty = !data || (
    data.contractual?.length === 0 &&
    data.revised?.length === 0 &&
    data.achieved?.length === 0
  );

  const handleDownloadPNG = () => {
    html2canvas(chartRef.current).then(canvas => {
      const link = document.createElement('a');
      link.download = 's-curve-chart.png';
      link.href = canvas.toDataURL();
      link.click();
    });
  };

  const handleDownloadCSV = () => {
    const csvRows = [];
    // Header
    csvRows.push(['Date', 'Contractual (%)', 'Revised (%)', 'Achieved (%)'].join(','));
    // Data
    chartData.forEach((d) => {
      const row = [
        d.date,
        d.contractual?.toFixed(2) || '',
        d.revised?.toFixed(2) || '',
        d.achieved?.toFixed(2) || ''
      ];
      csvRows.push(row.join(','));
    });
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 's-curve-data.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 bg-white rounded-lg shadow border">
        No S-Curve data available
      </div>
    );
  }

  // Merge all data points into a single array
  const allDataPoints = [
    ...(data.contractual || []).map(d => ({ ...d, type: 'contractual' })),
    ...(data.revised || []).map(d => ({ ...d, type: 'revised' })),
    ...(data.achieved || []).map(d => ({ ...d, type: 'achieved' }))
  ];

  // Group data by date
  const mergedData = allDataPoints.reduce((acc, curr) => {
    if (!acc[curr.date]) {
      acc[curr.date] = { date: curr.date };
    }
    acc[curr.date][curr.type] = curr.value;
    return acc;
  }, {});

  const mergedArray = Object.values(mergedData).sort((a, b) => a.date.localeCompare(b.date));

  // Determine view type from selectedYear
  const isAllYears = !selectedYear;
  let chartData;
  
  if (isAllYears) {
    // Yearly view: group by year, use the last available month in each year
    const grouped = {};
    mergedArray.forEach(item => {
      const year = item.date.split('-')[0];
      if (!grouped[year] || item.date > grouped[year].date) {
        grouped[year] = { ...item };
      }
    });
    chartData = Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  } else {
    // Monthly view: use each month as is (already sorted)
    chartData = mergedArray
      .filter(item => item.date.startsWith(selectedYear))
      .map(item => ({
        ...item,
        date: item.date.slice(0, 7) // 'YYYY-MM'
      }));
  }

  // Helper function to get month name
  const getMonthName = (dateString) => {
    const [year, month] = dateString.split('-');
    return new Date(year, month - 1).toLocaleString('default', { month: 'long' });
  };

  // Calculate latest values for summary cards
  const latestData = chartData[chartData.length - 1] || {};
  const variance = latestData.achieved - latestData.contractual;

  return (
    <div className="bg-white rounded-xl shadow border p-4 md:p-6 w-full max-w-full overflow-x-auto">
      <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-end">
        <button 
          onClick={handleDownloadPNG} 
          className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
        >
          <Image size={14} />
          <span>PNG</span>
        </button>
        <button 
          onClick={handleDownloadCSV}
          className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
        >
          <FileText size={14} />
          <span>CSV</span>
        </button>
        <button 
          onClick={() => setShowDetails(true)} 
          className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center space-x-1 text-sm shadow-sm"
        >
          <Eye size={14} />
          <span>Details</span>
        </button>
      </div>

      <div className="w-full min-w-[350px] md:min-w-[600px]" ref={chartRef}>
          <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 24, right: 32, left: 8, bottom: 48 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey="date"
              tickFormatter={(value) => {
                try {
                  if (isAllYears) {
                    return value.split('-')[0];
                  } else {
                    const [year, month] = value.split('-');
                    return new Date(year, month - 1).toLocaleString('default', { 
                      month: 'short', 
                      year: '2-digit' 
                    });
                  }
                } catch {
                  return value;
                }
              }}
              tick={{ fill: '#334155', fontSize: 13, fontWeight: 500 }}
              angle={-35}
              textAnchor="end"
              height={60}
              interval={0}
              minTickGap={10}
            />
            <YAxis
              tickFormatter={(value) => value.toFixed(2)}
              domain={[0, 100]}
              tick={{ fill: '#334155', fontSize: 13, fontWeight: 500 }}
              width={50}
              label={{ 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#64748b', fontSize: 14, fontWeight: 600 }
              }}
            />
            <Tooltip
              formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Progress']}
              labelFormatter={(label) => {
                try {
                  if (isAllYears) {
                    return label.split('-')[0];
                  } else {
                    const [year, month] = label.split('-');
                    return `${new Date(year, month - 1).toLocaleString('default', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}`;
                  }
                } catch {
                  return label;
                }
              }}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: 14
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: 10, paddingBottom: 10 }}
              iconType="circle"
              align="right"
              verticalAlign="top"
              formatter={(value) => (
                <span className="text-gray-700 font-medium text-sm">
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </span>
              )}
            />
            <Line
              type="monotone"
              dataKey="contractual"
              name="Contractual"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 3, fill: '#3b82f6', stroke: '#fff', strokeWidth: 1 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="revised"
              name="Revised"
              stroke="#f97316"
              strokeWidth={2}
              dot={{ r: 3, fill: '#f97316', stroke: '#fff', strokeWidth: 1 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="achieved"
              name="Achieved"
              stroke="#16a34a"
              strokeWidth={2}
              dot={{ r: 4, fill: '#16a34a', stroke: '#fff', strokeWidth: 1 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <Dialog
        header="S-Curve Detailed Data"
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        maximizable
        maximized={isMaximized}
        onMaximize={(e) => setIsMaximized(e.maximized)}
        style={{ width: isMaximized ? '95vw' : '75vw' }}
        className="min-w-[350px]"
      >
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Progress Overview</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="text-xs text-gray-500">Latest Contractual</p>
                <p className="text-lg font-semibold text-blue-600">
                  {latestData.contractual?.toFixed(2) || 0}%
                </p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="text-xs text-gray-500">Latest Revised</p>
                <p className="text-lg font-semibold text-orange-600">
                  {latestData.revised?.toFixed(2) || 0}%
                </p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="text-xs text-gray-500">Latest Achieved</p>
                <p className="text-lg font-semibold text-green-600">
                  {latestData.achieved?.toFixed(2) || 0}%
                </p>
              </div>
            </div>
          </div>

          {/* Detailed Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contractual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revised</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achieved</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chartData.map((row, index) => {
                  const contractual = parseFloat(row.contractual) || 0;
                  const achieved = parseFloat(row.achieved) || 0;
                  const variance = achieved - contractual;
                  const varianceClass = variance >= 0 ? 'text-green-600' : 'text-red-600';
                  const [year, month] = row.date.split('-');

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getMonthName(row.date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                        {row.contractual ? `${row.contractual.toFixed(2)}%` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                        {row.revised ? `${row.revised.toFixed(2)}%` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {row.achieved ? `${row.achieved.toFixed(2)}%` : '-'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${varianceClass}`}>
                        {row.contractual && row.achieved ? 
                          `${variance >= 0 ? '+' : ''}${variance.toFixed(2)}%` : 
                          '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default SCurveChartData;